// src/pages/educator/MyClass.js  —  route: /my-class
//
// A teacher manages their own class list:
//   * Add students — type names, and a sign-in is made for each (no email
//     needed). The list of sign-ins can be printed as cards.
//   * Pause / resume — a paused student stays on the list but gets nothing
//     through the class; a sign-in you made can't be used until resumed.
//   * Remove — end of the year, moved away. Sign-ins you made are deleted;
//     students who signed up themselves keep their own account.
//   * Reset a password (sign-ins you made).
// Everything is done by Cloud Functions (functions/classRoster.js), which
// check you own the class code and respect your class size.

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, onSnapshot } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../../firebase/firebaseConfig";
import useAccessLevel from "../../hooks/useAccessLevel";
import "./myClass.css";

const call = (name) => httpsCallable(functions, name);
const isSchoolLogin = (email) => String(email || "").toLowerCase().endsWith("@sbl.school");
const nameOf = (s) => `${s.firstName || ""} ${s.lastName || ""}`.trim() || s.email || "Student";

function parseNames(text) {
  return String(text || "")
    .split(/\n|,/)
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => {
      const parts = l.split(/\s+/);
      const first = parts[0];
      const last = parts.length > 1 ? parts[parts.length - 1].replace(/[^a-z]/gi, "").slice(0, 1) : "";
      return { firstName: first.replace(/[^a-z'\-]/gi, ""), lastInitial: last };
    })
    .filter((s) => s.firstName);
}

export default function MyClass() {
  const navigate = useNavigate();
  const { profile, user, loading } = useAccessLevel();
  const code = String(profile?.educatorCode || "").trim();
  const [dir, setDir] = useState(null);
  const [roster, setRoster] = useState(null);
  const [names, setNames] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState(null); // { tone, text }
  const [made, setMade] = useState(null); // last added sign-ins
  const [picked, setPicked] = useState(() => new Set());
  const [deleteLogins, setDeleteLogins] = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(null); // [uids]

  useEffect(() => {
    if (!code) return undefined;
    const u1 = onSnapshot(doc(db, "educatorDirectory", code), (s) => setDir(s.exists() ? s.data() : null), () => setDir(null));
    const u2 = onSnapshot(
      collection(db, "educatorRosters", code, "students"),
      (snap) => setRoster(snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => nameOf(a).localeCompare(nameOf(b)))),
      () => setRoster([])
    );
    return () => {
      u1();
      u2();
    };
  }, [code]);

  const owner = dir && user && dir.uid === user.uid;
  const cap = dir?.classAccess?.studentCap ?? null;
  const used = roster?.length || 0;
  const room = cap === null ? null : Math.max(0, cap - used);
  const toAdd = useMemo(() => parseNames(names), [names]);
  const paused = (roster || []).filter((s) => s.status === "hold");

  const say = (text, tone = "ok") => setMsg({ text, tone });
  const errText = (e) => String(e?.message || e || "Something went wrong.").replace(/^.*?:\s*/, "");

  const addStudents = async () => {
    if (!toAdd.length) return;
    setBusy("add");
    setMsg(null);
    try {
      const { data } = await call("teacherAddStudents")({ students: toAdd, password: password.trim() || undefined });
      setMade(data.made);
      setNames("");
      const bits = [`${data.made.length} sign-in${data.made.length === 1 ? "" : "s"} made`];
      if (data.skipped) bits.push(`${data.skipped} not added — your class is full`);
      if (data.failed?.length) bits.push(`${data.failed.length} couldn't be made (${data.failed[0].why})`);
      say(bits.join(" · "), data.skipped || data.failed?.length ? "warn" : "ok");
    } catch (e) {
      say(errText(e), "bad");
    } finally {
      setBusy("");
    }
  };

  const hold = async (uids, on) => {
    setBusy(on ? "hold" : "resume");
    setMsg(null);
    try {
      const { data } = await call("teacherSetStudentHold")({ studentUids: uids, hold: on });
      say(`${data.changed} student${data.changed === 1 ? "" : "s"} ${on ? "paused" : "back in class"}.`);
      setPicked(new Set());
    } catch (e) {
      say(errText(e), "bad");
    } finally {
      setBusy("");
    }
  };

  const remove = async (uids) => {
    setBusy("remove");
    setMsg(null);
    try {
      const { data } = await call("teacherRemoveStudents")({ studentUids: uids, deleteLogins });
      say(
        `${data.removed} student${data.removed === 1 ? "" : "s"} removed from your class${data.deleted ? ` (${data.deleted} sign-in${data.deleted === 1 ? "" : "s"} you made deleted)` : ""}.`
      );
      setPicked(new Set());
      setConfirmRemove(null);
    } catch (e) {
      say(errText(e), "bad");
    } finally {
      setBusy("");
    }
  };

  const resetPassword = async (s) => {
    const pw = window.prompt(`New password for ${nameOf(s)} (at least 6 characters).\nLeave empty to use your class password.`, "");
    if (pw === null) return;
    setBusy(`pw:${s.id}`);
    try {
      const { data } = await call("teacherResetStudentPassword")({ studentUid: s.id, password: pw.trim() || undefined });
      setMade([{ uid: s.id, name: nameOf(s), email: data.email, password: data.password }]);
      say(`New password set for ${nameOf(s)}.`);
    } catch (e) {
      say(errText(e), "bad");
    } finally {
      setBusy("");
    }
  };

  const toggle = (id) =>
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allPicked = roster?.length > 0 && picked.size === roster.length;

  if (loading) return <div className="mc-page"><p>Loading…</p></div>;
  if (!code) {
    return (
      <div className="mc-page">
        <div className="mc-card">
          <h1>My class</h1>
          <p>Your account doesn't have a class code yet. Open your educator dashboard once — a code is made for you — then come back here.</p>
          <button type="button" className="mc-btn" onClick={() => navigate(-1)}>← Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="mc-page">
      <header className="mc-head">
        <button type="button" className="mc-back" onClick={() => navigate(-1)}>← Back</button>
        <div>
          <h1>👩‍🏫 My class</h1>
          <p>
            Class code <b>{code}</b> · {used} student{used === 1 ? "" : "s"}
            {cap !== null ? ` of ${cap}` : ""}
            {paused.length ? ` · ${paused.length} paused` : ""}
          </p>
        </div>
      </header>

      {dir && !owner && (
        <p className="mc-msg is-bad">This class code belongs to another teacher, so you can't manage its list.</p>
      )}

      {msg && <p className={`mc-msg is-${msg.tone}`}>{msg.text}</p>}

      <section className="mc-card">
        <h2>➕ Add students</h2>
        <p className="mc-muted">
          One student per line — first name and last initial (for example <i>Maya R</i>). We make a sign-in for each one, no email
          needed. Students can also join on their own with your class code <b>{code}</b>.
        </p>
        <div className="mc-add">
          <textarea rows={5} value={names} onChange={(e) => setNames(e.target.value)} placeholder={"Maya R\nJordan T\nAva"} />
          <div className="mc-add-side">
            <label className="mc-field">
              Class password <small>optional — same for every student you add now</small>
              <input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Leave empty for the usual one" maxLength={40} />
            </label>
            <p className="mc-muted">
              {toAdd.length ? `${toAdd.length} to add` : "No names yet"}
              {room !== null ? ` · room for ${room} more` : ""}
            </p>
            <button type="button" className="mc-btn" onClick={addStudents} disabled={!owner || !toAdd.length || busy === "add" || room === 0}>
              {busy === "add" ? "Making sign-ins…" : room === 0 ? "Class is full" : `Add ${toAdd.length || ""} student${toAdd.length === 1 ? "" : "s"}`}
            </button>
            {room === 0 && (
              <button type="button" className="mc-link" onClick={() => navigate("/choose-plan")}>
                Need more room? See Classroom plans
              </button>
            )}
          </div>
        </div>
      </section>

      {made && made.length > 0 && (
        <section className="mc-card mc-made">
          <div className="mc-made-head">
            <h2>🔑 Sign-ins</h2>
            <button type="button" className="mc-btn is-soft" onClick={() => window.print()}>🖨 Print cards</button>
            <button type="button" className="mc-x" onClick={() => setMade(null)} aria-label="Hide">✕</button>
          </div>
          <p className="mc-muted">Write these down or print them — passwords aren't shown again (you can reset one any time).</p>
          <div className="mc-cards">
            {made.map((m) => (
              <div key={m.uid} className="mc-login">
                <b>{m.name}</b>
                <span>Sign in at smartboardlearning.com</span>
                <span>Email: <code>{m.email}</code></span>
                <span>Password: <code>{m.password}</code></span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="mc-card">
        <div className="mc-list-head">
          <h2>Students</h2>
          {roster?.length > 0 && (
            <div className="mc-bulk">
              <span className="mc-muted">{picked.size} selected</span>
              <button type="button" className="mc-btn is-soft" disabled={!picked.size || Boolean(busy)} onClick={() => hold([...picked], true)}>⏸ Pause</button>
              <button type="button" className="mc-btn is-soft" disabled={!picked.size || Boolean(busy)} onClick={() => hold([...picked], false)}>▶ Resume</button>
              <button type="button" className="mc-btn is-danger" disabled={!picked.size || Boolean(busy)} onClick={() => setConfirmRemove([...picked])}>Remove</button>
            </div>
          )}
        </div>
        <p className="mc-muted">New school year? Select last year's students and <b>Remove</b> them, then add your new class.</p>

        {roster === null ? (
          <p>Loading…</p>
        ) : !roster.length ? (
          <p className="mc-empty">No students yet. Add them above, or share your class code <b>{code}</b>.</p>
        ) : (
          <div className="mc-table-wrap">
            <table className="mc-table">
              <thead>
                <tr>
                  <th>
                    <input
                      type="checkbox"
                      checked={allPicked}
                      onChange={() => setPicked(allPicked ? new Set() : new Set(roster.map((s) => s.id)))}
                      aria-label="Select all"
                    />
                  </th>
                  <th>Student</th>
                  <th>Sign-in</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {roster.map((s) => {
                  const mine = s.madeByTeacher || isSchoolLogin(s.email);
                  const onHold = s.status === "hold";
                  return (
                    <tr key={s.id} className={onHold ? "is-hold" : ""}>
                      <td>
                        <input type="checkbox" checked={picked.has(s.id)} onChange={() => toggle(s.id)} aria-label={`Select ${nameOf(s)}`} />
                      </td>
                      <td>
                        <span className="mc-who">
                          {s.avatarUrl ? <img src={s.avatarUrl} alt="" /> : <span className="mc-ph">🙂</span>}
                          {nameOf(s)}
                        </span>
                      </td>
                      <td>
                        <code className="mc-email">{s.email || "—"}</code>
                        {mine ? <span className="mc-tag">made by you</span> : <span className="mc-tag is-own">own account</span>}
                      </td>
                      <td>{onHold ? <span className="mc-pill is-hold">Paused</span> : <span className="mc-pill">Active</span>}</td>
                      <td className="mc-row-actions">
                        <button type="button" className="mc-mini" disabled={Boolean(busy)} onClick={() => hold([s.id], !onHold)}>
                          {onHold ? "▶ Resume" : "⏸ Pause"}
                        </button>
                        {mine && (
                          <button type="button" className="mc-mini" disabled={Boolean(busy)} onClick={() => resetPassword(s)}>
                            {busy === `pw:${s.id}` ? "…" : "🔑 Password"}
                          </button>
                        )}
                        <button type="button" className="mc-mini is-danger" disabled={Boolean(busy)} onClick={() => setConfirmRemove([s.id])}>
                          Remove
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {confirmRemove && (
        <div className="mc-overlay" role="dialog" aria-modal="true" aria-label="Remove students">
          <div className="mc-dialog">
            <h3>Remove {confirmRemove.length} student{confirmRemove.length === 1 ? "" : "s"} from your class?</h3>
            <p>
              They come off your class list. Students who signed up on their own keep their account (they just aren't in your class
              any more).
            </p>
            <label className="mc-check">
              <input type="checkbox" checked={deleteLogins} onChange={(e) => setDeleteLogins(e.target.checked)} />
              Also delete the sign-ins I made for them (their coins and avatar go too)
            </label>
            <div className="mc-dialog-actions">
              <button type="button" className="mc-btn is-soft" onClick={() => setConfirmRemove(null)} disabled={busy === "remove"}>Cancel</button>
              <button type="button" className="mc-btn is-danger" onClick={() => remove(confirmRemove)} disabled={busy === "remove"}>
                {busy === "remove" ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
